(ns graph-ui.web.tracker-board
  "The widget kinds of the board of the tracker (IKC3/PLAN-ikc-v3-080-01/BOARD,
   screens 1 and 6 of the plan): :work-card, :board-column, :swimlane,
   :status-chain and :tracker-sidebar. Each one is declared in
   dsl/contexts/ui/meta-ui/v2.1-SNAPSHOT/, and each declaration holds the
   reasons. This namespace draws them.

   THE SERVER COMPOSES THE TEXT. Each card map comes from ui.board-web with
   its words and its dates written, so this namespace formats nothing and
   counts nothing. It draws, and each control sends ONE gesture.

   THE RULES ARE IN THE GATE. Each button sends its gesture, and the command
   decides. A refusal is the sentence of the status line of the board, never
   a control that is grey.

   EVERY CHILD LIST IS SPREAD WITH `into`. A bare vector of hiccup as one
   child is read by Reagent as a component call (CLAUDE.md)."
  (:require [clojure.string :as str]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.reopen :as reopen]
            [graph-ui.web.support :as support]
            [graph-ui.web.widgets :as widgets]
            [reagent.core :as r]
            ["@tabler/icons-react/dist/esm/icons/IconBug.mjs" :default IconBug]
            ["@tabler/icons-react/dist/esm/icons/IconBookmark.mjs" :default IconBookmark]
            ["@tabler/icons-react/dist/esm/icons/IconSquareCheck.mjs" :default IconSquareCheck]
            ["@tabler/icons-react/dist/esm/icons/IconBolt.mjs" :default IconBolt]
            ["@tabler/icons-react/dist/esm/icons/IconSubtask.mjs" :default IconSubtask]
            ["@tabler/icons-react/dist/esm/icons/IconBulb.mjs" :default IconBulb]
            ["@tabler/icons-react/dist/esm/icons/IconGavel.mjs" :default IconGavel]
            ["@tabler/icons-react/dist/esm/icons/IconChevronsUp.mjs" :default IconChevronsUp]
            ["@tabler/icons-react/dist/esm/icons/IconChevronUp.mjs" :default IconChevronUp]
            ["@tabler/icons-react/dist/esm/icons/IconEqual.mjs" :default IconEqual]
            ["@tabler/icons-react/dist/esm/icons/IconChevronDown.mjs" :default IconChevronDown]
            ["@tabler/icons-react/dist/esm/icons/IconChevronsDown.mjs" :default IconChevronsDown]
            ["@tabler/icons-react/dist/esm/icons/IconFlag.mjs" :default IconFlag]
            ["@tabler/icons-react/dist/esm/icons/IconPin.mjs" :default IconPin]
            ["@tabler/icons-react/dist/esm/icons/IconPinned.mjs" :default IconPinned]
            ["@tabler/icons-react/dist/esm/icons/IconLayoutKanban.mjs" :default IconLayoutKanban]
            ["@tabler/icons-react/dist/esm/icons/IconList.mjs" :default IconList]
            ["@tabler/icons-react/dist/esm/icons/IconRocket.mjs" :default IconRocket]
            ["@tabler/icons-react/dist/esm/icons/IconRoute.mjs" :default IconRoute]
            ["@tabler/icons-react/dist/esm/icons/IconUserCircle.mjs" :default IconUserCircle]
            ["@tabler/icons-react/dist/esm/icons/IconListDetails.mjs" :default IconListDetails]
            ["@tabler/icons-react/dist/esm/icons/IconHelp.mjs" :default IconHelp]))

;; @plan PLAN-ikc-v3-080-01-the-tracker-v2
;; @adr ADR-graph-ui-v0-013-01-one-layout-model

(def ^:private muted "text-[var(--muted)]")

(defn- fire!
  "Dispatch the command of `template` with `value` as $value."
  [bus session-id template value]
  (when (and bus template)
    (js-invoke bus "dispatch"
               (bind/fill-command template {'$session-id session-id '$value value})
               (fn [_] nil))))

(defn- item-uuid [id] (if (string? id) (uuid id) id))

(defn- gesture!
  "Send the gesture `g` of a card. REOPEN OPENS ITS DIALOG first (screen 1b
   of PLAN-ikc-v3-080-01): the dialog of lane REPORT asks for the reason and
   the failed criteria, and it sends :workflow/reopen. The declared command of
   the gesture is sent too, so the card is selected under the dialog."
  [bus session-id props g card]
  (when (and (= :reopen g) bus (:item-id card))
    (reopen/open! {:bus bus :item-id (item-uuid (:item-id card))}))
  (fire! bus session-id (bind/gesture-template props g) card))

;; ---------------------------------------------------------------------------
;; The icons of a work type and of a priority
;; ---------------------------------------------------------------------------

(def type-icons
  "PURE DATA: the icon and the colour token of each work type. The colour is a
   token of the theme, never a literal (ADR-graph-ui-v0-017-01 §4)."
  {"bug"             [IconBug         "var(--crit)"]
   "story"           [IconBookmark    "var(--good)"]
   "task"            [IconSquareCheck "var(--accent)"]
   "epic"            [IconBolt        "var(--warn)"]
   "sub-task"        [IconSubtask     "var(--accent)"]
   "feature-request" [IconBulb        "var(--good)"]
   "decision"        [IconGavel       "var(--warn)"]})

(def priority-icons
  "PURE DATA: the arrow and the colour token of each priority."
  {"highest" [IconChevronsUp   "var(--crit)"]
   "high"    [IconChevronUp    "var(--crit)"]
   "medium"  [IconEqual        "var(--warn)"]
   "low"     [IconChevronDown  "var(--accent)"]
   "lowest"  [IconChevronsDown "var(--accent)"]})

(defn- type-icon [type type-label]
  (let [[Icon colour] (get type-icons (str type) [IconSquareCheck "var(--muted)"])]
    [:span {:class "inline-flex items-center justify-center rounded-sm shrink-0"
            :title (or type-label type)
            :aria-label (or type-label type)
            :style {:width "1.1em" :height "1.1em" :background colour :color "var(--panel)"}}
     [:> Icon {:size 13 :stroke 2.5}]]))

(defn- priority-icon [priority label]
  (when-let [[Icon colour] (get priority-icons (str priority))]
    [:span {:class "inline-flex items-center shrink-0" :title (or label priority)
            :aria-label (or label priority) :style {:color colour}}
     [:> Icon {:size 16 :stroke 2.5}]]))

(defn- avatar [initials assignee]
  [:span {:class (str "inline-flex items-center justify-center rounded-full shrink-0 text-[10px] font-semibold "
                      "border border-[var(--border)] bg-[var(--raised)] text-[var(--text)]")
          :title (or assignee "nobody")
          :style {:width "1.75em" :height "1.75em"}}
   (or (not-empty initials) "–")])

;; ---------------------------------------------------------------------------
;; :work-card
;; ---------------------------------------------------------------------------

(def ^:private action-class
  {"accept" "rounded px-2 py-0.5 text-xs font-semibold bg-[var(--accent)] text-white"
   "reopen" (str "rounded px-2 py-0.5 text-xs border border-[var(--border)] "
                 "text-[var(--text)] hover:bg-[var(--hover)]")})

(defn card-view
  "One card as hiccup. `on-gesture` is (fn [gesture-keyword card]), or nil for
   a card that sends nothing. PUBLIC so :board-column draws the same card."
  [{:keys [item-id key title type type-label priority priority-label assignee initials
           label footer link flag tone selected actions] :as card}
   draggable on-gesture]
  (let [danger? (= "danger" (str tone))]
    [:div (cond-> {:class (str "flex flex-col gap-1 rounded-md border bg-[var(--card,var(--panel))] p-2 text-sm "
                               "min-w-0 "
                               (if selected "border-[var(--accent)] " "border-[var(--border)] ")
                               (when on-gesture "cursor-pointer hover:bg-[var(--hover)] "))
                   :style {:box-shadow "var(--elevation-raised)"}
                   :data-card-key key
                   :data-item-id item-id
                   :role "button"
                   :tab-index 0}
            on-gesture (assoc :on-click (fn [_] (on-gesture :select card))
                              :on-key-down (fn [e] (when (= "Enter" (.-key e)) (on-gesture :select card))))
            draggable (assoc :draggable true
                             :on-drag-start (fn [e]
                                              (let [dt (.-dataTransfer e)]
                                                (.setData dt widgets/drag-mime (widgets/row->json card))
                                                (set! (.-effectAllowed dt) "move")))))
     [:div {:class (str "leading-snug break-words line-clamp-2 "
                        (if danger? "text-[var(--danger)]" "text-[var(--text)]"))}
      title]
     (when (seq label)
       [:div [:span {:class "inline-block rounded px-1.5 text-[11px] font-semibold bg-[var(--accent-soft)] text-[var(--accent)]"}
              label]])
     (when (seq flag)
       [:div {:class "flex items-center gap-1 text-xs font-semibold text-[var(--danger)]"}
        [:> IconFlag {:size 13 :stroke 2.5}] flag])
     ;; THE ROW WRAPS on a narrow column, so the avatar goes under the key
     ;; and is never cut by the edge of the card.
     [:div {:class "flex flex-wrap items-center gap-x-2 gap-y-1"}
      [type-icon type type-label]
      [:span {:class "font-mono text-xs font-semibold text-[var(--text)] whitespace-nowrap"} key]
      [priority-icon priority priority-label]
      [:span {:class "flex-1"}]
      [avatar initials assignee]]
     (when (or (seq footer) link)
       (into [:div {:class (str "border-t border-[var(--border)] pt-1 text-xs " muted)}]
             (cond-> [(str footer)]
               link (conj (when (seq footer) " · ")
                          [:a {:href (:href link) :target "_blank" :rel "noopener"
                               :class "underline text-[var(--accent)]"
                               :on-click (fn [e] (.stopPropagation e))}
                           (:label link)]))))
     (when (and on-gesture (seq actions))
       ;; THE BUTTONS WRAP: a narrow column has room for two, and Deployed
       ;; has three (IKC3/PLAN-ikc-v3-080-01/FLOW34).
       (into [:div {:class "flex flex-wrap gap-2 pt-1"}]
             (for [{:keys [gesture label]} actions]
               [:button {:class (get action-class gesture (get action-class "reopen"))
                         :data-action gesture
                         :on-click (fn [e] (.stopPropagation e) (on-gesture (keyword gesture) card))}
                label])))]))

(defn widget-work-card
  "A work item as a card. Gestures :select, :accept and :reopen, $value the
   card map."
  [{:keys [value draggable] :as props} bus session-id]
  (let [on (fn [g card] (gesture! bus session-id props g card))]
    (when value
      [card-view value draggable (when bus on)])))

;; ---------------------------------------------------------------------------
;; :board-column
;; ---------------------------------------------------------------------------

(defn- heading-text [label n]
  (str (str/upper-case (str label)) (when n (str " · " n))))

(defn column-heading-style
  "PURE: the style of the heading of a column. STICKY at the top of the
   scroller of the board, above the cards (--layer-sticky), with the
   background of the column (fault report IKC-13)."
  [danger?]
  {:position   "sticky"
   :top        0
   :z-index    "var(--layer-sticky)"
   :background (if danger? "var(--panel)" "var(--raised)")})

(defn widget-board-column
  "One status category as a column of cards. Gestures :drop ($dragged the
   card map), and :select, :accept and :reopen of its cards ($value the card
   map)."
  [_props _bus _session-id]
  (let [over? (r/atom false)]
    (fn [{:keys [category label note tone value] :as props} bus session-id]
      (let [{:keys [absent cards groups more empty-label weight]} value
            n       (or (:count value) (+ (count cards) (reduce + 0 (map (comp count :cards) groups))))
            drop-t  (bind/gesture-template props :drop)
            on      (when bus (fn [g card] (gesture! bus session-id props g card)))
            drag?   (boolean drop-t)
            danger? (= :danger (keyword (or tone :default)))
            mark    (widgets/drop-mark-style @over?)
            card    (fn [c] ^{:key (:item-id c)} [card-view c drag? on])]
        (when-not absent
          (into [:div (cond-> {:class (str "flex flex-col gap-2 rounded-md p-2 min-w-[9rem] basis-0 "
                                           (if danger?
                                             "border border-dashed border-[var(--danger)] "
                                             "bg-[var(--raised)] "))
                               :style {:flex-grow (or weight 1)}
                               :data-category category}
                        (and drop-t bus) (merge (widgets/drop-attrs drop-t bus session-id over?))
                        mark (update :style merge mark))
                ;; THE HEADING STAYS AT THE TOP (fault report IKC-13, George,
                ;; 2026-10-18: "Column headers hide on scroll"). It is sticky in
                ;; the scroller of the board, and it covers the cards that pass
                ;; under it, so it has the background of its column.
                [:div {:class "flex flex-col"
                       :data-column-heading category
                       :style (column-heading-style danger?)}
                 [:div {:class (str "text-xs font-bold tracking-wide "
                                    (if danger? "text-[var(--danger)]" "text-[var(--text)]"))
                        :data-heading category}
                  (heading-text (or (:label value) label category) n)]
                 (when-let [nt (or (:note value) note)]
                   [:div {:class (str "text-xs " muted)} nt])]]
                (concat
                 (map card cards)
                 (for [{:keys [heading link] :as g} groups]
                   ^{:key heading}
                   (into [:div {:class "flex flex-col gap-2"}]
                         (cons
                          [:div {:class "rounded px-2 py-1 text-xs bg-[var(--accent-soft)]"}
                           [:div {:class "font-semibold text-[var(--accent)]"} heading]
                           (when link
                             [:a {:href (:href link) :class "underline text-[var(--accent)]"} (:label link)])]
                          (map card (:cards g)))))
                 (when (and (empty? cards) (empty? groups) empty-label)
                   [[:div {:class (str "text-xs italic " muted)} empty-label]])
                 (when more
                   [[:a {:href (:href more) :class "text-xs underline text-[var(--accent)]"} (:label more)]]))))))))

;; ---------------------------------------------------------------------------
;; :swimlane
;; ---------------------------------------------------------------------------

(defn lane-hidden?
  "PURE: does a :swimlane draw nothing? Only with :hide-when-empty and a bound
   :value whose :count is 0."
  [{:keys [hide-when-empty value]}]
  (boolean (and hide-when-empty (map? value) (zero? (or (:count value) 0)))))

(defn widget-swimlane
  "A row of :board-column children, each as tall as the longest one, under an
   optional title."
  [{:keys [title tone value] :as props} children]
  (when-not (lane-hidden? props)
    (let [t       (or (:title value) title)
          danger? (= :danger (keyword (or tone :default)))]
      [:div {:class "flex flex-col gap-1 min-w-0" :data-swimlane (or t "lane")}
       (when t
         [:div {:class (str "text-xs font-bold tracking-wide "
                            (if danger? "text-[var(--danger)]" "text-[var(--text)]"))}
          (str "⇈ " (str/upper-case t) (when-let [n (:count value)] (str " · " n)))])
       (into [:div {:class "flex gap-3 items-stretch min-w-0"}] children)])))

;; ---------------------------------------------------------------------------
;; :status-chain
;; ---------------------------------------------------------------------------

(def ^:private group-style
  {"to-do"       {:background "var(--raised)"      :color "var(--text)"}
   "in-progress" {:background "var(--accent-soft)" :color "var(--accent)"}
   "done"        {:background "var(--accent-soft)" :color "var(--good)"}})

(defn widget-status-chain
  "The statuses of the workflow in order, as boxes with arrows."
  [{:keys [value]}]
  (into [:div {:class "flex flex-wrap items-center justify-center gap-2" :data-status-chain "true"}]
        (interpose
         [:span {:class (str "text-lg " muted)} "→"]
         (for [{:keys [label note group]} value]
           [:div {:class "flex flex-col items-center rounded-md px-4 py-2 min-w-[7rem]"
                  :style (get group-style (str group) (get group-style "to-do"))
                  :data-status label}
            [:div {:class "text-sm font-bold"} label]
            (when note [:div {:class "text-xs"} note])]))))

;; ---------------------------------------------------------------------------
;; :tracker-sidebar
;; ---------------------------------------------------------------------------

(def ^:private pin-key "ikc.tracker.sidebar.pinned")

(defn- stored-pin []
  (try (= "true" (.getItem js/localStorage pin-key)) (catch :default _ false)))

(defn- store-pin! [v]
  (try (.setItem js/localStorage pin-key (str (boolean v))) (catch :default _ nil)))

(def sidebar-icons
  {"board" IconLayoutKanban "backlog" IconList "releases" IconRocket
   "workflow" IconRoute "work" IconUserCircle "progress" IconListDetails "help" IconHelp})

(defn widget-tracker-sidebar
  "The links of the tracker. Folded to icons, or pinned open with names."
  [_props]
  (let [pinned (r/atom (stored-pin))]
    (fn [{:keys [items]}]
      (into [:nav {:class (str "flex flex-col gap-1 border-r border-[var(--border)] p-1 shrink-0 "
                               (if @pinned "w-48" "w-11"))
                   :aria-label "Tracker"
                   :data-pinned (str @pinned)}
             [:button {:class (str "flex items-center gap-2 rounded p-1.5 hover:bg-[var(--hover)] " muted)
                       :title (if @pinned "Unpin: fold the sidebar to icons" "Pin: keep the sidebar open")
                       :on-click (fn [_] (store-pin! (swap! pinned not)))}
              [:> (if @pinned IconPinned IconPin) {:size 18}]
              (when @pinned [:span {:class "text-xs"} "Unpin"])]]
            (for [{:keys [label href icon current]} items]
              [:a {:href href
                   :title label
                   :aria-current (when current "page")
                   :class (str "flex items-center gap-2 rounded p-1.5 text-sm hover:bg-[var(--hover)] "
                               (if current "bg-[var(--accent-soft)] text-[var(--accent)]" "text-[var(--text)]"))}
               [:> (get sidebar-icons (str icon) IconList) {:size 18}]
               (when @pinned [:span label])])))))

;; ---------------------------------------------------------------------------
;; :report-button
;; ---------------------------------------------------------------------------

(defn- island-of-url []
  (try (.get (js/URLSearchParams. (.. js/window -location -search)) "island")
       (catch :default _ nil)))

(defn widget-report-button
  "Report or + Create in the top bar of a tracker screen. Each one opens the
   dialog of lane REPORT (graph-ui.web.support): Report takes the picture of
   the screen first, and Create takes none."
  [{:keys [label form]} bus session-id]
  (let [create? (= :create (keyword (or form :report)))
        ctx     {:bus bus :session-id session-id :island (island-of-url)}]
    [:button {:class (str "rounded px-3 py-1 text-sm font-semibold shrink-0 "
                          (if create?
                            "bg-[var(--accent)] text-white"
                            "border border-[var(--border)] text-[var(--text)] hover:bg-[var(--hover)]"))
              :data-report-form (if create? "create" "report")
              :disabled (nil? bus)
              :on-click (fn [_] (if create? (support/open-create! ctx) (support/report-fault! ctx)))}
     (or label (if create? "+ Create" "Report"))]))
